/* RYZE hero slider — THE PROGRESS RAIL (rebuilt 2026-08-04).
 *
 * The swipe itself is native CSS scroll-snap. This script tracks the current
 * slide, advances the deck, routes the film slides, and draws the progress
 * rail under the CTAs.
 *
 * ══ THE PROGRESS RAIL ══
 *
 * One clock, one render, one state. Everything the rail shows is a pure
 * function of {index, running, segStart, heldFrac} evaluated at paint time:
 *
 *     past segments   -> full
 *     future segments -> empty
 *     the current one -> its fraction
 *
 * and the fraction has exactly two sources of truth:
 *     a film slide  -> the film's own currentTime / duration, so the bar can
 *                      never disagree with the pixels, stalls when the film
 *                      stalls, and lands exactly when the film ends;
 *     a still slide -> the wall clock against STILL_MS.
 *
 * Fills are drawn with transform: scaleX on a compositor layer and the CSS
 * carries NO transition. W-PERFFIX (30 Oct 2026): a still's fill is ONE
 * Web Animation (scaleX 0 -> 1 over STILL_MS, linear, compositor-run),
 * re-created from the same truth on every state change, and the advance is
 * one timer to the segment's end; only a FILM slide keeps the per-frame
 * redraw (its bar follows currentTime). The per-frame loop used to run for
 * every still the whole time the hero was on screen (4.5 s of main thread
 * in PageSpeed's trace). The look is the same: 3.4 s linear fill, frozen
 * under a finger, reset on every change. That is what retires both
 * photographed bugs:
 *   - the wrap-around used to animate every bar back to zero in chorus,
 *     because each width write rode a 250ms CSS transition; a scaleX redraw
 *     with no transition repaints the whole rail in one frame;
 *   - a manual swipe used to leave the rail frozen mid-fill, because the
 *     per-segment rAF/setTimeout pair was cancelled on touch and nothing
 *     owned the pixels until autoplay resumed; here the ONE render loop
 *     redraws truth on the very next frame, whatever happened.
 *
 * The same clock advances the deck itself (fraction >= 1 -> next, plus the
 * film's own `ended` event), so the timer and the bar are one mechanism and
 * can never drift apart. The loop parks while the hero is off screen or the
 * tab is hidden, and every return path — scroll-back, tab return, bfcache
 * restore (pageshow) — restarts the current segment from zero instead of
 * guessing at lost time.
 *
 * Interference SUSPENDS, it never REWINDS (owner-caught 2026-08-04: the old
 * resume restarted the segment, which on a film slide reset currentTime and
 * replayed the film once more before moving on). While a finger is down,
 * a still freezes at its held fraction and a film keeps its own clock with
 * the bar following truthfully; only the auto-advance is gated. On release,
 * the segment CONTINUES from wherever it stands - a still resumes its
 * clock mid-fraction, a film that already ended advances on the next frame.
 * A swipe that lands on a new slide starts that slide immediately, exactly
 * as if the deck had advanced there itself: no hold, no replay, the flow
 * simply carries on from the slide the user chose.
 *
 * Reusable: everything rail-specific is the five members below plus
 * renderRail/segFraction/segDuration and the loop; the rest is standard
 * slider plumbing. Lift them as a unit.
 */

(function () {
  'use strict';

  var STILL_MS = 3400; // how long an image frame holds (owner 2026-08-03: faster; films still run their full length)
  var VIDEO_FALLBACK_MS = 9500; // if a film never reports its duration
  var SETTLE_MS = 450; // breathing room after a release, for scroll-snap to land
  /* W-PERFFIX: an engine without Web Animations keeps the old per-frame redraw for the stills too */
  var NO_WAAPI = typeof Element === 'undefined' || typeof Element.prototype.animate !== 'function';

  /* THE TWO DECKS (dev2.15-hero2, 30 Oct 2026): the phone slider and the desktop plate deck (from 750 px: slide A, the
     collection photograph; slide B, the family render) run on this ONE engine. The only difference is which attributes
     mark the track, the slides and the dots: the desktop deck lives inside the phone deck's root (.ryze-hero), so it wears
     its own data-hero-desk-* names and the phone's queries can never reach it. Everything else (the clock, STILL_MS, the
     rail, scroll-snap, pause on a finger, reduced motion, the off-screen park) is shared. */
  var PHONE_SEL = { track: '[data-hero-track]', slide: '[data-hero-slide]', dot: '[data-hero-dot]' };
  var DESK_SEL = { track: '[data-hero-desk-track]', slide: '[data-hero-desk-slide]', dot: '[data-hero-desk-dot]' };

  function HeroSlider(root, sel) {
    sel = sel || PHONE_SEL;
    this.root = root;
    this.track = root.querySelector(sel.track);
    this.slides = Array.prototype.slice.call(root.querySelectorAll(sel.slide));
    this.dots = Array.prototype.slice.call(root.querySelectorAll(sel.dot));
    /* Every film slide, not just one (2026-08-04: the 100 ml offer film
       joined the collection film). Each video loads on approach and plays
       only while its own slide is current. */
    this.videos = Array.prototype.slice.call(root.querySelectorAll('.ryze-hero__video'));

    if (!this.track || this.slides.length < 2) return;

    /* THE ADAPTIVE FRAME (2026-08-17, owner order): the element whose height
       follows the active slide, so a shorter artwork never leaves a band of
       section background under itself. */
    this.viewport = this.track.closest('.ryze-hero__viewport') || root;

    this.fills = this.dots.map(function (dot) {
      return dot.querySelector('.ryze-hero__dot-fill');
    });
    /* Last painted fraction per segment, so a frame that changes nothing
       writes nothing. -1 forces the first paint. */
    this.painted = this.dots.map(function () {
      return -1;
    });

    this.index = 0;
    this.userTookOver = false;
    this.pointerDown = false;
    this.running = false; // is the current segment advancing the deck
    this.segStart = 0; // wall-clock birth of the current segment
    this.heldFrac = null; // frozen fraction while the user holds the deck
    this.rafId = null;
    this.onScreen = false;
    this.resumeTimer = null;
    this.anim = null; // the current still's fill animation (W-PERFFIX)
    this.animFill = null;
    this.animSeg = 0;
    this.advT = null; // the timer to the current still's end
    this.waiting = null; // THE HOLD: the slide the deck waits on
    this.loaded = false; // THE SLIDES WAIT: true once the page has painted its largest element (wakeAfterPaint)
    this.holdT = null; // THE HOLD's ceiling

    /* Before flex has sized the track, every slide sits at offset 0 and the
       observer reports them all as visible at once, so the last one wins and
       the slider starts on the wrong frame. Ignore observer traffic until a
       frame has been painted and the track actually has width. */
    this.settled = false;
    this.reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var self = this;
    this.loopBound = function (now) {
      self.loop(now);
    };

    this.observeSlides();
    this.bind();
    this.applyIndex(0);
    this.wakeAfterPaint();

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.settled = true;
        /* W-PERFFIX (the refuter's P1): the deck starts on the slide the track actually shows. A swipe made before
           this script ran (a slow link) leaves the track off slide 1; forcing index 0 there left the frame on screen
           unrequested until the first advance. With no early swipe the track sits at 0, exactly as before. */
        var w = self.track.clientWidth || 1;
        var i0 = Math.max(0, Math.min(Math.round(self.track.scrollLeft / w), self.slides.length - 1));
        self.index = i0;
        self.applyIndex(i0);
        self.restartSegment();
      });
    });
  }

  /* ---------- the rail ---------- */

  HeroSlider.prototype.segDuration = function () {
    var film = this.videoIn(this.index);
    if (film) {
      var d = film.duration;
      return d && isFinite(d) && d > 0 ? d * 1000 + 250 : VIDEO_FALLBACK_MS;
    }
    return STILL_MS;
  };

  HeroSlider.prototype.segFraction = function (now) {
    if (this.heldFrac != null) return this.heldFrac;
    var film = this.videoIn(this.index);
    if (film && film.getAttribute('src') && film.duration && isFinite(film.duration) && film.duration > 0) {
      return Math.min(film.currentTime / film.duration, 1);
    }
    /* Reduced motion never auto-advances, and a bar that fills toward an
       advance that will never come reads as broken, so stills hold at zero;
       a film the user chose to watch still reports its real progress above. */
    if (this.reduced) return 0;
    return Math.min((now - this.segStart) / this.segDuration(), 1);
  };

  /* The rail, painted from the one truth. Past segments full, future empty
     (written only when they change); the current one is a film's bar (the
     per-frame loop below follows currentTime) or a still's ONE animation. */
  HeroSlider.prototype.renderRail = function (force) {
    var now = performance.now();
    var film = this.videoIn(this.index);
    for (var d = 0; d < this.fills.length; d += 1) {
      if (d === this.index) continue;
      var v = d < this.index ? 1 : 0;
      if (this.anim && this.animFill === this.fills[d]) {
        this.dropAnim(v);
        this.painted[d] = v;
      } else if (force || v !== this.painted[d]) {
        this.painted[d] = v;
        if (this.fills[d]) this.fills[d].style.transform = 'scaleX(' + v + ')';
      }
    }
    if (film || NO_WAAPI) {
      if (this.anim) this.dropAnim(this.segFraction(now));
      this.paintFilm(force);
    } else {
      this.paintStill(now);
    }
    this.schedule(now, film);
  };

  HeroSlider.prototype.paintFilm = function (force) {
    var d = this.index;
    var v = this.segFraction(performance.now());
    if (force || Math.abs(v - this.painted[d]) > 0.001) {
      this.painted[d] = v;
      if (this.fills[d]) this.fills[d].style.transform = 'scaleX(' + v + ')';
    }
  };

  /* A still's fill: ONE linear animation from where the truth stands to the
     segment's end, keyed to segStart, so a repaint with the same truth keeps
     the running animation (no restart, no jump). Held (a finger), reduced
     motion, off screen or a hidden tab: the bar rests where the truth says,
     with no animation. */
  HeroSlider.prototype.paintStill = function (now) {
    var d = this.index;
    var fill = this.fills[d];
    if (!fill) return;
    var frac = this.segFraction(now);
    var live =
      this.heldFrac == null && !this.reduced && this.onScreen && !document.hidden &&
      typeof fill.animate === 'function' && frac < 1;
    if (!live) {
      if (this.anim) this.dropAnim(frac);
      this.painted[d] = frac;
      fill.style.transform = 'scaleX(' + frac + ')';
      return;
    }
    var a = this.anim;
    if (a && this.animFill === fill && this.animSeg === this.segStart && a.playState !== 'idle') return;
    if (a) this.dropAnim(frac);
    var dur = this.segDuration();
    fill.style.transform = 'scaleX(' + frac + ')';
    a = fill.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
      duration: dur,
      easing: 'linear',
      fill: 'forwards',
    });
    a.currentTime = Math.max(0, Math.min(now - this.segStart, dur));
    this.anim = a;
    this.animFill = fill;
    this.animSeg = this.segStart;
    this.painted[d] = -1; // the animation owns this fill now
  };

  /* Ends the running animation with the bar left at v (the inline value is
     written first, in the same task, so no frame can show a gap). */
  HeroSlider.prototype.dropAnim = function (v) {
    var a = this.anim;
    if (!a) return;
    if (this.animFill) this.animFill.style.transform = 'scaleX(' + v + ')';
    this.anim = null;
    this.animFill = null;
    a.cancel();
  };

  /* What moves the deck next: a still gets ONE timer to its segment's end; a
     film rides the per-frame loop (its own clock is the truth); held, reduced,
     off screen or hidden, nothing is armed. */
  HeroSlider.prototype.schedule = function (now, film) {
    if (this.advT) {
      clearTimeout(this.advT);
      this.advT = null;
    }
    if (!this.onScreen || document.hidden) {
      this.stopLoop();
      return;
    }
    if (film || NO_WAAPI) {
      this.startLoop();
      return;
    }
    this.stopLoop();
    if (!this.running || this.userTookOver || this.heldFrac != null) return;
    var self = this;
    var left = this.segDuration() - (now - this.segStart);
    this.advT = setTimeout(function () {
      self.advT = null;
      self.tick();
    }, Math.max(0, left));
  };

  HeroSlider.prototype.tick = function () {
    if (!this.onScreen || document.hidden || this.videoIn(this.index)) return;
    if (!this.running || this.userTookOver || this.heldFrac != null) return;
    var now = performance.now();
    if (this.segFraction(now) >= 1) this.advance();
    else this.schedule(now, null);
  };

  /* The film slide's loop: the bar follows the film frame by frame (and every slide's, without Web Animations). */
  HeroSlider.prototype.loop = function (now) {
    this.rafId = null;
    if (!this.onScreen || document.hidden) return; // parked; a wake path restarts it
    if (!NO_WAAPI && !this.videoIn(this.index)) return; // stills ride their animation
    this.paintFilm(false);
    if (this.running && !this.userTookOver && this.heldFrac == null && this.segFraction(now) >= 1) {
      this.advance();
      if (!NO_WAAPI && !this.videoIn(this.index)) return;
    }
    this.rafId = requestAnimationFrame(this.loopBound);
  };

  HeroSlider.prototype.startLoop = function () {
    if (this.rafId == null) this.rafId = requestAnimationFrame(this.loopBound);
  };

  HeroSlider.prototype.stopLoop = function () {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  /* ---------- THE SLIDES WAIT + THE HOLD (W-PERFFIX, 30 Oct 2026) ----------
     The phone slides after the first carry data-src / data-srcset (see
     sections/ryze-hero.liquid). wake(i) gives slide i its real address. The
     moment the page has painted its largest element (the first LCP entry;
     WebKit: the first contentful paint; 3 s net) the next slide wakes at high
     priority, so it starts after the LCP and still about as early as main's
     parse-time request did; a slide that becomes current wakes itself and its
     successor (one ahead, never the whole deck: a visitor who scrolls away
     downloads no more than main did); a finger on the track wakes both
     neighbours (and the one after) at high priority, a dot its slide and
     neighbours. THE HOLD: the auto-advance never lands on a frame whose image
     has not decoded; the bar rests full until it has (a file that fails counts
     as arrived), and after 8 s on a request that never settles the deck skips
     to the next frame that has loaded, so it can never stall for good. The
     desktop deck has no data-src and its plates are always ready. */
  HeroSlider.prototype.imgOf = function (i) {
    var slide = this.slides[i];
    return slide ? slide.querySelector('img') : null;
  };

  HeroSlider.prototype.wake = function (i, priority) {
    var n = this.slides.length;
    var img = this.imgOf(((i % n) + n) % n);
    if (!img) return;
    var set = img.getAttribute('data-srcset');
    var src = img.getAttribute('data-src');
    if (!set && !src) return;
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
    if (priority) img.fetchPriority = priority;
    if (this.hSync) img.addEventListener('load', this.hSync, { once: true });
    if (set) img.setAttribute('srcset', set);
    if (src) img.setAttribute('src', src);
    this.predecode(img);
  };

  /* decode() resolves once the image has loaded AND decoded (it rejects on a
     failed file: that counts as arrived, so THE HOLD can never stall the deck). */
  HeroSlider.prototype.predecode = function (img) {
    if (img._ryzeDecode) return img._ryzeDecode;
    var mark = function () {
      img._ryzeDecoded = true;
    };
    if (img.loading === 'lazy') img.loading = 'eager';
    /* arrival without decode(), and the net under a decode() that rejects before the file has landed (WebKit has
       rejected a decode() whose request was still in flight): wait for load or error before calling it arrived */
    var arrive = function () {
      return new Promise(function (res) {
        if (img.complete) return res();
        img.addEventListener('load', res, { once: true });
        img.addEventListener('error', res, { once: true });
      });
    };
    img._ryzeDecode = (typeof img.decode === 'function' ? img.decode().then(null, arrive) : arrive()).then(mark);
    return img._ryzeDecode;
  };

  /* the visitor reached for the deck: the frames either side (and the one after) at high priority */
  HeroSlider.prototype.wakeAround = function (i) {
    this.wake(i, 'high');
    this.wake(i + 1, 'high');
    this.wake(i - 1, 'high');
    this.wake(i + 2);
  };

  /* AT THE FIRST PAINT (W-PERFFIX): fn runs once the page has painted its largest contentful element (the first LCP
     entry; WebKit without LCP: the first contentful paint; neither: two frames; a 3 s net). A request started after
     that paint is outside the LCP's window, whatever the load event is doing (on a loaded machine the first frame
     can even follow the load event). */
  function atFirstPaint(fn) {
    var done = false;
    var go = function () {
      if (done) return;
      done = true;
      setTimeout(fn, 0);
    };
    var types = (window.PerformanceObserver && PerformanceObserver.supportedEntryTypes) || [];
    var type = types.indexOf('largest-contentful-paint') !== -1 ? 'largest-contentful-paint' : types.indexOf('paint') !== -1 ? 'paint' : null;
    setTimeout(go, 3000);
    if (!type) {
      requestAnimationFrame(function () {
        requestAnimationFrame(go);
      });
      return;
    }
    try {
      var po = new PerformanceObserver(function () {
        po.disconnect();
        go();
      });
      po.observe({ type: type, buffered: true });
    } catch (e) {
      go();
    }
  }

  HeroSlider.prototype.wakeAfterPaint = function () {
    var self = this;
    atFirstPaint(function () {
      self.loaded = true;
      self.wake(self.index, 'high');
      self.wake(self.index + 1, 'high');
    });
  };

  /* THE HOLD's way out: the first frame after n (in deck order) whose image has arrived (or the film) */
  HeroSlider.prototype.nextLoaded = function (n) {
    for (var k = 0; k < this.slides.length - 1; k += 1) {
      var j = (n + k) % this.slides.length;
      if (j === this.index) continue;
      var img = this.imgOf(j);
      if (!img) return j;
      if (!img.hasAttribute('data-srcset') && !img.hasAttribute('data-src') && img.complete && img.naturalWidth > 0) return j;
    }
    return -1;
  };

  HeroSlider.prototype.isReady = function (i) {
    var img = this.imgOf(i);
    if (!img) return true; // the film slide: its poster and film arrive one slide early (loadVideo)
    if (img.hasAttribute('data-srcset') || img.hasAttribute('data-src')) return false;
    return !!img._ryzeDecoded;
  };

  /* Advance, or hold on a full bar until the next frame has decoded. */
  HeroSlider.prototype.advance = function () {
    var n = (this.index + 1) % this.slides.length;
    if (this.isReady(n)) {
      this.next();
      return;
    }
    if (this.waiting === n) return;
    this.waiting = n;
    this.wake(n);
    var self = this;
    var from = this.index;
    var done = function () {
      if (self.waiting !== n) return;
      self.waiting = null;
      if (self.holdT) {
        clearTimeout(self.holdT);
        self.holdT = null;
      }
      if (
        self.index === from && self.running && !self.userTookOver && self.heldFrac == null &&
        self.onScreen && !document.hidden && self.segFraction(performance.now()) >= 1
      ) {
        self.next();
      }
    };
    this.predecode(this.imgOf(n)).then(done, done);
    /* the ceiling: a request that never settles (neither load nor error) must not hold the deck for good */
    if (this.holdT) clearTimeout(this.holdT);
    this.holdT = setTimeout(function () {
      self.holdT = null;
      if (self.waiting !== n || self.index !== from) return;
      if (!(self.running && !self.userTookOver && self.heldFrac == null && self.onScreen && !document.hidden)) return;
      var j = self.nextLoaded(n);
      if (j === -1) return;
      self.waiting = null;
      self.goTo(j);
    }, 8000);
  };

  /* A fresh segment from zero: the resume path, the return-to-tab path, the
     bfcache path and the initial arm all come through here, so no path can
     invent a mid-flight fraction. */
  HeroSlider.prototype.restartSegment = function () {
    this.segStart = performance.now();
    this.heldFrac = null;
    this.running = !this.reduced && !this.userTookOver;
    var film = this.videoIn(this.index);
    if (film && film.getAttribute('src')) {
      try {
        film.currentTime = 0;
      } catch (e) {
        /* not seekable yet; it will play from the start anyway */
      }
      if (this.onScreen && !document.hidden) this.playVideo(film);
    }
    this.renderRail(true);
  };

  /* ---------- which slide is on screen ---------- */

  HeroSlider.prototype.observeSlides = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) return;

    this.io = new IntersectionObserver(
      function (entries) {
        if (!self.settled) return;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
          var i = self.slides.indexOf(entry.target);
          if (i !== -1 && i !== self.index) {
            self.index = i;
            self.applyIndex(i);
          }
        });
      },
      { root: this.track, threshold: [0.6] }
    );

    this.slides.forEach(function (s) {
      self.io.observe(s);
    });

    // Separate, looser watch just for fetching each film a slide early.
    this.videos.forEach(function (video) {
      var vSlide = video.closest('[data-hero-slide]');
      if (!vSlide) return;
      var pre = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              self.loadVideo(video);
              pre.disconnect();
            }
          });
        },
        { root: self.track, rootMargin: '0px 100% 0px 100%' }
      );
      pre.observe(vSlide);
    });
  };

  HeroSlider.prototype.loadVideo = function (video) {
    if (!video || video.getAttribute('src')) return;
    /* dev2.14-perf: the poster rides with the src (data-poster), never at parse time */
    if (video.dataset.poster && !video.getAttribute('poster')) video.setAttribute('poster', video.dataset.poster);
    var src = video.dataset.src;
    if (!src) return;
    video.setAttribute('src', src);
    video.load();
  };

  HeroSlider.prototype.bind = function () {
    var self = this;

    /* THE ADAPTIVE FRAME's two follow-ups: the frame re-measures when the
       viewport changes shape, and when any slide image actually loads (the
       safety net for a stale width/height attribute pair). rAF-coalesced so
       a resize storm costs one measurement per frame. */
    var hSync = function () {
      if (self.hRaf) return;
      self.hRaf = requestAnimationFrame(function () {
        self.hRaf = null;
        self.syncHeight();
      });
    };
    this.hSync = hSync;
    window.addEventListener('resize', hSync);
    window.addEventListener('orientationchange', hSync);
    this.slides.forEach(function (s) {
      var img = s.querySelector('img');
      if (img && !img.complete) img.addEventListener('load', hSync, { once: true });
    });

    this.dots.forEach(function (dot, d) {
      dot.addEventListener('click', function () {
        // An explicit choice: jump there and let the slide start at once. The dot's own position in the rail is its
        // slide (dev2.15-hero2: the desktop deck's dots carry data-hero-desk-dot, so the old dataset.heroDot read
        // NaN there; on the phone both are the same number, forloop.index0).
        self.wakeAround(d);
        self.goTo(d);
      });
    });

    /* Pause while a finger is actually on the slider, then resume once it has
       been left alone. Deliberately NOT listening for wheel: a wheel event
       fires on the track whenever someone scrolls the page with the cursor
       over the hero, which would otherwise stop the slider for good on an
       ordinary downward scroll. */
    ['pointerdown', 'touchstart'].forEach(function (evt) {
      self.track.addEventListener(
        evt,
        function () {
          self.wakeAround(self.index); // a finger on the deck: the frames either side, now, at high priority
          self.pause();
        },
        { passive: true }
      );
    });

    ['pointerup', 'touchend', 'touchcancel', 'pointercancel'].forEach(function (evt) {
      self.track.addEventListener(
        evt,
        function () {
          self.resumeSoon();
        },
        { passive: true }
      );
    });

    /* A film that reaches its final frame advances the deck itself; the
       clock's fraction >= 1 check is the belt for a film that stalls at the
       end and for the stills. */
    this.videos.forEach(function (v) {
      v.addEventListener('ended', function () {
        if (!self.running || self.userTookOver || self.heldFrac != null) return;
        if (self.videoIn(self.index) === v) self.advance();
      });
    });

    // Never let a film play, nor the clock run, while the hero is off screen.
    if ('IntersectionObserver' in window) {
      var vis = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              self.onScreen = true;
              self.startLoop();
              if (!self.userTookOver) {
                self.restartSegment();
              } else {
                self.renderRail(true);
              }
            } else {
              self.onScreen = false;
              self.stopLoop();
              self.running = false;
              self.pauseVideo();
              self.renderRail(false); // the bar rests where it stands; nothing armed
            }
          });
        },
        /* threshold 0, not 0.25. This observer's root is the viewport, so its
           intersectionRatio is capped at visibleHeight / heroHeight, and the hero
           is deliberately taller than the viewport. Any threshold above
           viewportHeight/heroHeight can therefore NEVER be met, and because
           autoplay and the film both hang off this callback, the whole slider
           would sit still with no console error to say why. At 4:5 on a 1440
           viewport the cap is 0.402, so 0.25 happened to pass, but a short window
           (1440x400 -> 0.222) already failed silently before the 9:16 work.
           The callback only ever reads isIntersecting, so the threshold was
           buying nothing in exchange for that trap. */
        { threshold: 0 }
      );
      vis.observe(this.root);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.stopLoop();
        self.running = false;
        self.pauseVideo();
        self.renderRail(false);
      } else if (self.onScreen) {
        self.startLoop();
        if (!self.userTookOver) self.restartSegment();
        else self.renderRail(true);
      }
    });

    /* Coming back through the back/forward cache restores the page mid-state:
       the scroll position is real but every clock is stale. Re-derive the
       index from the DOM and arm a fresh segment. */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      self.userTookOver = false;
      self.syncIndexFromScroll();
      if (self.onScreen && !document.hidden) {
        self.startLoop();
        self.restartSegment();
      }
    });
  };

  /* Finger down: SUSPEND. A still freezes at its held fraction; a film keeps
     playing with the bar following its clock (freezing the bar under moving
     pixels would be a lie). Only the auto-advance is gated. */
  HeroSlider.prototype.pause = function () {
    this.pointerDown = true;
    this.userTookOver = true;
    this.running = false;
    if (!this.videoIn(this.index)) {
      this.heldFrac = this.segFraction(performance.now());
    }
    if (this.resumeTimer) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
    }
    this.renderRail(false); // freeze the still's bar, disarm the advance
  };

  /* Release: CONTINUE, never rewind. A short settle lets scroll-snap land
     (a landed swipe resumes earlier, through applyIndex). A frozen still
     re-arms its clock mid-fraction; a film needs nothing - its currentTime
     is the truth, and if it already ended the next frame advances. */
  HeroSlider.prototype.resumeSoon = function () {
    var self = this;
    this.pointerDown = false;
    if (this.reduced) return;
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(function () {
      self.resumeTimer = null;
      self.resume();
    }, SETTLE_MS);
  };

  HeroSlider.prototype.resume = function () {
    this.userTookOver = false;
    this.running = !this.reduced;
    if (this.heldFrac != null) {
      this.segStart = performance.now() - this.heldFrac * this.segDuration();
      this.heldFrac = null;
    }
    var film = this.videoIn(this.index);
    if (film && this.onScreen && !document.hidden) this.playVideo(film);
    this.renderRail(true);
  };

  /* After a manual swipe the internal index can lag the real scroll position,
     so it is re-derived from the DOM before autoplay picks up again. */
  HeroSlider.prototype.syncIndexFromScroll = function () {
    var w = this.track.clientWidth || 1;
    var i = Math.round(this.track.scrollLeft / w);
    this.index = Math.max(0, Math.min(i, this.slides.length - 1));
    this.applyIndex(this.index);
  };

  HeroSlider.prototype.videoIn = function (i) {
    var slide = this.slides[i];
    if (!slide) return null;
    for (var k = 0; k < this.videos.length; k += 1) {
      if (slide.contains(this.videos[k])) return this.videos[k];
    }
    return null;
  };

  HeroSlider.prototype.isVideoSlide = function (i) {
    return !!this.videoIn(i);
  };

  /* The one door for "slide i is now current": aria, film routing, and a
     fresh segment for the new slide. Under a held deck the new segment holds
     at zero; released, it starts from zero. Either way the rail is redrawn
     to truth this same frame. */
  HeroSlider.prototype.applyIndex = function (i) {
    if (this.loaded || i !== 0) {
      this.wake(i, 'high');
      this.wake(i + 1);
    }
    this.dots.forEach(function (dot, d) {
      if (d === i) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    var film = this.videoIn(i);
    if (film) {
      this.loadVideo(film);
      if (film.getAttribute('src')) {
        try {
          film.currentTime = 0;
        } catch (e) {
          /* metadata not ready; it plays from the start regardless */
        }
      }
      if (this.onScreen && !document.hidden) this.playVideo(film);
    }
    this.pauseVideo(film);

    this.segStart = performance.now();
    if (this.pointerDown) {
      /* The finger is still down (a slow drag crossed the threshold): hold
         the new slide at zero until release; a film's bar follows its clock. */
      this.heldFrac = film ? null : 0;
      this.running = false;
    } else {
      /* A landed swipe, a dot, an auto-advance, or an init: the new slide
         starts NOW, exactly as if the deck had walked here itself. */
      this.heldFrac = null;
      this.running = !this.reduced;
      this.userTookOver = false;
      if (this.resumeTimer) {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = null;
      }
    }
    this.renderRail(true);
    this.syncHeight();
  };

  /* THE ADAPTIVE FRAME. The viewport takes the ACTIVE slide's own rendered
     height (the CSS transitions it), so the hero's bottom edge is always the
     artwork's own foot: a short frame pulls the section up, a tall one runs
     past the fold exactly as before. The value is written twice on purpose -
     as the animated inline height, and as --ryze-hero-h, which the CSS min()
     reads to re-anchor the scrim and the controls onto the artwork whenever
     its foot sits above the fold line. Each slide's width/height attributes
     reserve the true ratio box before its image loads, so offsetHeight is
     already correct for lazy frames; the load listener in bind() is only the
     safety net for a stale attribute. Phones only: the desktop hero is a
     different construct and keeps its own sizing. */
  HeroSlider.prototype.syncHeight = function () {
    if (window.innerWidth >= 750) {
      this.viewport.style.height = '';
      this.viewport.style.removeProperty('--ryze-hero-h');
      return;
    }
    var slide = this.slides[this.index];
    if (!slide) return;
    var h = slide.offsetHeight;
    if (!h) return;
    this.viewport.style.height = h + 'px';
    this.viewport.style.setProperty('--ryze-hero-h', h + 'px');
  };

  HeroSlider.prototype.playVideo = function (video) {
    if (!video) return;
    var p = video.play();
    // Autoplay can still be refused; that is not an error worth surfacing.
    if (p && typeof p.catch === 'function') p.catch(function () {});
  };

  /* Pauses every film except `except` (pass nothing to silence them all), so
     an image slide silences everything and a film slide silences the others. */
  HeroSlider.prototype.pauseVideo = function (except) {
    this.videos.forEach(function (v) {
      if (v !== except && !v.paused) v.pause();
    });
  };

  HeroSlider.prototype.goTo = function (i) {
    var target = this.slides[i];
    if (!target) return;
    this.index = i;
    this.track.scrollTo({
      left: target.offsetLeft - this.track.offsetLeft,
      behavior: this.reduced ? 'auto' : 'smooth',
    });
    this.applyIndex(i);
  };

  HeroSlider.prototype.next = function () {
    this.goTo((this.index + 1) % this.slides.length);
  };

  /* Above 750px the slider is display:none and the landscape still is showing
     instead, so there is nothing to drive. Constructing it anyway would attach
     three IntersectionObservers, start an autoplay timer and fetch the film for a
     slider nobody can see. `phone` is also watched, so a window dragged narrow
     mid-visit gets a working slider rather than a dead one; the reverse direction
     needs no teardown because the observers are all rooted on elements that have
     just been display:none'd, which stops them reporting. */
  var phone = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

  function init() {
    if (phone && !phone.matches) return;
    Array.prototype.forEach.call(document.querySelectorAll('.ryze-hero'), function (root) {
      if (root.dataset.heroReady) return;
      root.dataset.heroReady = '1';
      new HeroSlider(root);
    });
  }

  if (phone) {
    if (phone.addEventListener) {
      phone.addEventListener('change', init);
    } else if (phone.addListener) {
      phone.addListener(init);
    }
  }

  /* The desktop plate deck (dev2.15-hero2): from 750 px the still is two plates on the same engine. Below 750 it is
     display:none and never constructed (the mirror of the phone guard above); a window dragged wide mid-visit gets it.
     Slide B's <img> is lazy so it never competes with slide A (the LCP); once the page has loaded it is asked for at
     once, so the first advance never lands on an empty field. */
  var desk = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;

  function initDesk() {
    if (!desk || !desk.matches) return;
    Array.prototype.forEach.call(document.querySelectorAll('[data-hero-desk]'), function (root) {
      if (root.dataset.heroReady) return;
      root.dataset.heroReady = '1';
      new HeroSlider(root, DESK_SEL);
      var wake = function () {
        Array.prototype.forEach.call(root.querySelectorAll('img[loading="lazy"]'), function (img) {
          img.loading = 'eager';
        });
      };
      if (document.readyState === 'complete') {
        wake();
      } else {
        window.addEventListener('load', wake, { once: true });
      }
    });
  }

  if (desk) {
    if (desk.addEventListener) {
      desk.addEventListener('change', initDesk);
    } else if (desk.addListener) {
      desk.addListener(initDesk);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('DOMContentLoaded', initDesk);
  } else {
    init();
    initDesk();
  }
})();
